import { useCallback, useEffect, useState } from 'react';

import {
  getAuthSession,
  loginWithPassword,
  logoutSession,
} from '../api/client.js';

export interface AuthenticationState {
  authenticated: boolean;
  loading: boolean;
  submitting: boolean;
  error: string | null;
  login: (password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  retry: () => Promise<void>;
}

function errorMessage(error: unknown): string {
  return error instanceof Error
    ? error.message
    : 'Die Anmeldung konnte nicht geprüft werden.';
}

export function useAuthentication(): AuthenticationState {
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retry = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const session = await getAuthSession();
      setAuthenticated(session.authenticated);
      setError(null);
    } catch (caught) {
      setAuthenticated(false);
      setError(errorMessage(caught));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const initialCheck = window.setTimeout(() => void retry(), 0);
    const handleUnauthorized = (): void => setAuthenticated(false);
    window.addEventListener('satismap:unauthorized', handleUnauthorized);
    return () => {
      window.clearTimeout(initialCheck);
      window.removeEventListener('satismap:unauthorized', handleUnauthorized);
    };
  }, [retry]);

  const login = useCallback(async (password: string): Promise<boolean> => {
    setSubmitting(true);
    try {
      const result = await loginWithPassword(password);
      setAuthenticated(result.authenticated);
      setError(null);
      return result.authenticated;
    } catch (caught) {
      setAuthenticated(false);
      setError(errorMessage(caught));
      return false;
    } finally {
      setSubmitting(false);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    setSubmitting(true);
    try {
      await logoutSession();
    } catch {
      // The local view is locked even if the server is temporarily unreachable.
    } finally {
      setAuthenticated(false);
      setSubmitting(false);
    }
  }, []);

  return { authenticated, loading, submitting, error, login, logout, retry };
}
